'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { matterReferenceAllowsDemoFlag } from '@/lib/utils/demo-matter';

interface DemoMatterCheckboxProps {
  /** The matter reference currently held by the form. */
  reference: string | null | undefined;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Unique within the page — several forms render this control. */
  id?: string;
}

/**
 * Offers to mark a matter as a demo matter — and only appears once the matter's
 * reference says `DEMO`.
 *
 * The reference gate is what makes this safe to put in front of a lawyer. A demo
 * matter is never billed, never reaches the firm's practice management system,
 * sends every external email back to the user instead of to the bank, and runs
 * its title searches against a sandbox registry. None of that is visible on the
 * matter afterwards, so the reference is the one place the decision is written
 * down where the next person to open it will read it.
 *
 * Both the create and edit forms render this, so the flag can be taken back off —
 * a matter marked by mistake is invisible to billing and to the firm's PMS, and
 * recovering it must not need an engineer.
 *
 * The gate here decides only whether to *offer* the option; the server enforces
 * the same rule on the write and rejects a mismatch. Submit through
 * `resolveDemoMatterFlag` so a box ticked before the reference was edited cannot
 * keep submitting `true` from behind a hidden control.
 */
export function DemoMatterCheckbox({
  reference,
  checked,
  onCheckedChange,
  id = 'is-demo-matter'
}: DemoMatterCheckboxProps) {
  if (!matterReferenceAllowsDemoFlag(reference)) {
    return null;
  }

  return (
    <div className="flex items-start gap-x-2">
      <Checkbox
        id={id}
        name={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="mt-0.5"
      />
      <div className="space-y-0.5">
        <Label htmlFor={id} className="font-medium">
          This is a demo matter
        </Label>
        <p className="text-muted-foreground text-xs">
          For training and walkthroughs. It is never billed, never syncs to your
          practice management system, and every email it sends comes back to you
          instead of going to the real recipient.
        </p>
      </div>
    </div>
  );
}
